/**
 * A simple tab strip.
 *
 * One screen at a time, kept mounted only while it is showing — a tab holding
 * its own query (as `CustomCertificateTab` does) must not fetch until an
 * admin actually opens it, following the same "only load what is visible"
 * rule `ActivityBreakdown` and `renderExpanded` use elsewhere in this screen.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';
import styles from './Tabs.module.css';

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  active: string;
  onChange: (id: string) => void;
  /** Read by assistive tech; name the group, not each tab. */
  label: string;
}

export function Tabs({ tabs, active, onChange, label }: TabsProps) {
  const baseId = useId();
  const current = tabs.find((tab) => tab.id === active) ?? tabs[0];

  return (
    <div>
      <div role="tablist" aria-label={label} className={styles.list}>
        {tabs.map((tab) => {
          const selected = tab.id === current?.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${baseId}-${tab.id}-tab`}
              aria-selected={selected}
              aria-controls={`${baseId}-${tab.id}-panel`}
              tabIndex={selected ? 0 : -1}
              className={selected ? styles.tabActive : styles.tab}
              onClick={() => onChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {current && (
        <div
          role="tabpanel"
          id={`${baseId}-${current.id}-panel`}
          aria-labelledby={`${baseId}-${current.id}-tab`}
        >
          {current.content}
        </div>
      )}
    </div>
  );
}
